import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiX, FiCheck, FiUser } from 'react-icons/fi';
import Pagination from '../utils/pagination2';

interface User {
    id: number;
    name: string;
    email?: string;
    phone_number?: string;
    role?: string;
}

interface Option {
    value: string | number;
    label: string;
    description?: string;
    data?: any;
}

interface PaginationData {
    current_page: number;
    per_page: number;
    total: number;
    total_pages: number;
    has_next_page: boolean;
    has_prev_page: boolean;
    next_page: number | null;
    prev_page: number | null;
}

interface MultiSelectModalProps {
    label: string;
    placeholder?: string;
    selectedIds: number[];
    onChange: (ids: number[]) => void;
    fetchOptions: (page: number, search?: string, limit?: number) => Promise<{
        data: Option[];
        pagination: PaginationData;
    }>;
    required?: boolean;
    error?: string;
    disabled?: boolean;
    searchPlaceholder?: string;
    debounceMs?: number;
    renderOption?: (option: Option) => React.ReactNode;
}

const MultiSelectModal: React.FC<MultiSelectModalProps> = ({
    label,
    placeholder = 'Select recipients',
    selectedIds,
    onChange,
    fetchOptions,
    required = false,
    error,
    disabled = false,
    searchPlaceholder = 'Search users...',
    debounceMs = 300,
    renderOption,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [options, setOptions] = useState<Option[]>([]);
    const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [pagination, setPagination] = useState<PaginationData | null>(null);
    const [loading, setLoading] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [allSelectedOptionsMap, setAllSelectedOptionsMap] = useState<Map<number, Option>>(new Map());

    const loadOptions = useCallback(async (page: number, search?: string, limit?: number) => {
        setLoading(true);
        try {
            const result = await fetchOptions(page, search, limit);
            setOptions(result.data);
            setPagination(result.pagination);

            // Store any selected options from this page
            result.data.forEach(option => {
                const id = Number(option.value);
                if (selectedIds.includes(id)) {
                    setAllSelectedOptionsMap(prev => new Map(prev).set(id, option));
                }
            });

            return result;
        } catch (error) {
            console.error('Error loading options:', error);
            setOptions([]);
            setPagination(null);
            return { data: [], pagination: null };
        } finally {
            setLoading(false);
        }
    }, [fetchOptions, selectedIds]);

    const handleSearch = (query: string) => {
        setSearchQuery(query);
        if (searchTimeout) clearTimeout(searchTimeout);

        const timeout = setTimeout(() => {
            setCurrentPage(1);
            loadOptions(1, query, perPage);
        }, debounceMs);

        setSearchTimeout(timeout);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        loadOptions(newPage, searchQuery, perPage);
    };

    const handleLimitChange = (newLimit: number) => {
        setPerPage(newLimit);
        setCurrentPage(1);
        loadOptions(1, searchQuery, newLimit);
    };

    const toggleOption = (option: Option) => {
        const id = Number(option.value);
        const isSelected = selectedIds.includes(id);

        if (isSelected) {
            onChange(selectedIds.filter(i => i !== id));
            setAllSelectedOptionsMap(prev => {
                const newMap = new Map(prev);
                newMap.delete(id);
                return newMap;
            });
        } else {
            onChange([...selectedIds, id]);
            setAllSelectedOptionsMap(prev => new Map(prev).set(id, option));
        }
    };

    const removeOption = (id: number) => {
        onChange(selectedIds.filter(i => i !== id));
        setAllSelectedOptionsMap(prev => {
            const newMap = new Map(prev);
            newMap.delete(id);
            return newMap;
        });
    };

    const clearAll = () => {
        onChange([]);
        setAllSelectedOptionsMap(new Map());
    };

    const openModal = () => {
        if (disabled) return;
        setIsModalOpen(true);
        loadOptions(1, '', perPage);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSearchQuery('');
        setCurrentPage(1);
    };

    // Update selectedOptions from the map whenever selectedIds changes
    useEffect(() => {
        const newSelectedOptions = selectedIds
            .map(id => allSelectedOptionsMap.get(id))
            .filter((option): option is Option => option !== undefined);
        setSelectedOptions(newSelectedOptions);
    }, [selectedIds, allSelectedOptionsMap]);

    useEffect(() => {
        return () => {
            if (searchTimeout) clearTimeout(searchTimeout);
        };
    }, [searchTimeout]);

    return (
        <>
            <div className="w-full mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                </label>

                <div
                    onClick={openModal}
                    className={`min-h-[42px] px-4 py-2 border rounded-lg cursor-pointer transition-all ${error ? 'border-red-500' : 'border-gray-300 hover:border-gray-400'
                        } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:bg-gray-50'}`}
                >
                    {selectedOptions.length === 0 ? (
                        <span className="text-gray-500">{placeholder}</span>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {selectedOptions.map((option) => (
                                <span
                                    key={option.value}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm"
                                >
                                    <FiUser className="w-3 h-3" />
                                    {option.label}
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeOption(Number(option.value));
                                        }}
                                        className="ml-1 hover:text-blue-900"
                                    >
                                        <FiX className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

                {selectedOptions.length > 0 && (
                    <div className="mt-2 flex items-center justify-between text-sm">
                        <span className="text-gray-600">
                            {selectedOptions.length} recipient{selectedOptions.length !== 1 ? 's' : ''} selected
                        </span>
                        <button
                            type="button"
                            onClick={clearAll}
                            className="text-red-600 hover:text-red-700 font-medium"
                        >
                            Clear All
                        </button>
                    </div>
                )}
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">{label}</h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    {selectedIds.length} selected
                                </p>
                            </div>
                            <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <FiX className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 border-b border-gray-200">
                            <div className="relative">
                                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => handleSearch(e.target.value)}
                                    placeholder={searchPlaceholder}
                                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 min-h-[300px]">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                                    <p className="text-gray-600">Loading...</p>
                                </div>
                            ) : options.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                        <FiUser className="w-8 h-8 text-gray-400" />
                                    </div>
                                    <p className="text-gray-600 text-center">
                                        {searchQuery ? 'No users found matching your search' : 'No users available'}
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {options.map((option) => {
                                        const isSelected = selectedIds.includes(Number(option.value));
                                        return (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => toggleOption(option)}
                                                className={`w-full px-4 py-3 rounded-lg text-left transition-all flex items-center justify-between ${isSelected
                                                    ? 'bg-blue-50 border-2 border-blue-500'
                                                    : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100 hover:border-gray-300'
                                                    }`}
                                            >
                                                <div className="flex-1">
                                                    {renderOption ? (
                                                        renderOption(option)
                                                    ) : (
                                                        <>
                                                            <div className="font-medium text-gray-900">
                                                                {option.label}
                                                            </div>
                                                            {option.description && (
                                                                <div className="text-sm text-gray-500 mt-0.5">
                                                                    {option.description}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                                {isSelected && (
                                                    <FiCheck className="w-5 h-5 text-blue-600 flex-shrink-0 ml-3" />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="border-t border-gray-200 px-6 py-4 flex justify-between items-center">
                            <button
                                onClick={clearAll}
                                disabled={selectedIds.length === 0}
                                className="px-4 py-2 text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Clear All ({selectedIds.length})
                            </button>
                            <button
                                onClick={closeModal}
                                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                            >
                                Done
                            </button>
                        </div>

                        {/* Pagination Footer using Pagination component */}
                        {pagination && pagination.total > 0 && (
                            <div className="border-t border-gray-200">
                                <Pagination
                                    pagination={pagination}
                                    onPageChange={handlePageChange}
                                    onLimitChange={handleLimitChange}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default MultiSelectModal;